import { useState } from "react";
import { useParams } from "react-router-dom";
import { Link2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HasAccess } from "@/components/common/has-access";
import { toast } from "@/hooks/use-toast";
import useBundlesGrouped from "@/features/bundles/hooks/use-bundles-grouped";
import useActiveRegions from "@/features/regions/hooks/use-active-regions";
import { ALL_REGIONS_CODE } from "@/features/regions/constants";
import {
  getUserCohort,
  getOnboardingBundle,
  UserCohort,
  OnboardingBundle,
} from "@/features/bundles/services/get-user-cohort";
import { getDashboardBaseUrl } from "@/lib/dashboard-url";

// CRM standalone tool: enter a user's phone + pick a product → generate a public
// "Choose a Bundle" link showing that user's cohort prices (via an `as_of` anchor
// resolved server-side from their created_at). The care team never picks a price.
export default function ChooseBundleLinkTool() {
  const { region: urlRegion } = useParams<{ region: string }>();
  const regions = useActiveRegions().data ?? [];

  // Region selector — prefilled with the current (URL) region; drives which
  // products/prices are resolved.
  //
  // `all` is the default landing segment and means "no region filter" for the
  // lists elsewhere in the CRM, but a pricing link is always for ONE region: its
  // prices, its bundles, and a visibility toggle that is per-region. So it does
  // not seed a selection here — the same guard products.tsx spells `specificRegion`.
  const [selectedRegion, setSelectedRegion] = useState<string>(
    urlRegion && urlRegion !== ALL_REGIONS_CODE ? urlRegion : "",
  );

  // Every product LAUNCHED in the selected region, i.e. one with an ACTIVE
  // bundle there — the same definition the Products screen uses. The grouped
  // endpoint is already region-filtered, so its groups are exactly the launched
  // products, and each carries `on_dashboard` (product ACTIVE + active bundle +
  // the per-region metadata.visible toggle).
  //
  // Deliberately NOT filtered by that visibility toggle: it only governs whether
  // the customer sees the card on their own dashboard, and a product hidden
  // there is precisely the one the care team needs a shareable link for. The
  // link itself resolves fine — /bundle resolution never gates on `visible`.
  // Hidden ones are badged in the picker instead.
  const { data: grouped, isLoading: isLoadingProducts } = useBundlesGrouped({
    region: selectedRegion,
    enabled: !!selectedRegion,
  });
  const products = (grouped?.groups ?? [])
    .filter(
      (g) =>
        !!g.product &&
        g.product.status === "ACTIVE" &&
        // An offering whose ACTIVE rows were all retired is price history, not a
        // launched offering.
        g.offerings.some(
          (o) => !!o.current || (o.experimental?.length ?? 0) > 0,
        ),
    )
    .map((g) => ({
      id: g.product!.id,
      name: g.product!.name,
      hidden: g.product!.on_dashboard === false,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const [phone, setPhone] = useState("");
  const [productId, setProductId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    link: string;
    cohort: UserCohort;
    onboarding: OnboardingBundle | null;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const canGenerate = !!phone.trim() && !!productId && !!selectedRegion;

  const generate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setResult(null);
    try {
      // Partial phone is fine — the server does a contains match (e.g.
      // "8334026705" resolves "+918334026705"). Passing productId also resolves
      // whether this user has onboarded that product (→ has_onboarded).
      const cohort = await getUserCohort(phone.trim(), selectedRegion, productId);
      const resolvedRegion = cohort.region ?? selectedRegion ?? "in";
      const params = new URLSearchParams();
      params.set("product_id", productId);
      params.set("region", resolvedRegion);
      if (cohort.as_of) params.set("as_of", cohort.as_of);
      // Only when the user has actually onboarded THIS product do we flag it, so
      // the dashboard shows the standard bundle UI instead of the mandatory
      // onboarding-intro UI. A resolved-but-not-onboarded user (or a new/unknown
      // user) omits it → onboarding-intro UI.
      if (cohort.has_onboarded) params.set("has_onboarded", "true");
      const link = `${getDashboardBaseUrl()}/choose-a-bundle?${params.toString()}`;
      // The onboarding bundle for this product+region, resolved to the same
      // cohort (as_of) as the link.
      const onboarding = await getOnboardingBundle(
        productId,
        resolvedRegion,
        cohort.as_of,
      );
      setResult({ link, cohort, onboarding });
      // Copy immediately for convenience.
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast({
        title: "Pricing Catalog link ready",
        description: cohort.user_found
          ? "Copied. Shows this user's cohort prices."
          : "Copied. New/unknown user — shows the latest prices.",
      });
    } catch (e: unknown) {
      toast({
        title: "Couldn't generate link",
        description:
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || "Failed to resolve the user's pricing",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!result) return;
    void navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <HasAccess permission="read">
      <div className="max-w-xl mx-auto p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Link2 className="h-5 w-5" /> Pricing Catalog
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enter a customer&apos;s phone and pick a product — we generate a public
            pricing page link that shows <b>their</b> price (based on when they
            signed up). Pricing is resolved automatically.
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Region</p>
            <Select
              value={selectedRegion}
              onValueChange={(v) => {
                setSelectedRegion(v);
                setProductId(""); // launched products differ per region
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a region" />
              </SelectTrigger>
              <SelectContent>
                {regions.map((r) => (
                  <SelectItem key={r.id} value={r.code}>
                    {r.display_name} ({r.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Customer phone</p>
            <Input
              placeholder="Search phone (e.g. 8334026705)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Partial numbers work — we match by contains (no country code
              needed).
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1">Product</p>
            <Select value={productId} onValueChange={setProductId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a product" />
              </SelectTrigger>
              <SelectContent>
                {/* Radix renders a bare popover for an empty list — say why.
                    "None launched" is only true once the query has answered;
                    while it is in flight the list is empty for a different
                    reason and must not claim the region is empty. */}
                {products.length === 0 && (
                  <p className="px-2 py-3 text-xs text-gray-400">
                    {!selectedRegion
                      ? "Select a region first."
                      : isLoadingProducts
                        ? "Loading products…"
                        : "No products launched in this region yet."}
                  </p>
                )}
                {products.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      {p.name}
                      {/* Launched but toggled off the customer dashboard — they
                          can only reach it through this link. */}
                      {p.hidden && (
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                          Hidden
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button disabled={!canGenerate || loading} onClick={generate}>
            {loading ? "Generating…" : "Generate & copy link"}
          </Button>
        </div>

        {result && (
          <div className="space-y-3">
            {/* Confirmation: who this link resolved to. */}
            <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-700">
                Resolved customer
              </p>
              {result.cohort.user_found ? (
                <div className="text-sm">
                  <p className="font-medium text-gray-900">
                    {result.cohort.full_name ?? "—"}
                  </p>
                  <p className="text-gray-500">
                    {result.cohort.phone ?? "no phone on file"}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Cohort:{" "}
                    {result.cohort.as_of
                      ? `signed up ${new Date(
                          result.cohort.as_of,
                        ).toLocaleDateString()} (their prices)`
                      : "baseline"}{" "}
                    · region {result.cohort.region ?? selectedRegion ?? "in"}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-amber-600">
                  No matching user — treated as a new customer (latest prices).
                </p>
              )}

              {/* Lead info (funnel record). */}
              <div className="border-t border-gray-100 pt-2">
                <p className="text-[11px] font-medium text-gray-500 mb-1">
                  Lead
                </p>
                {result.cohort.lead ? (
                  <div className="text-xs text-gray-600 space-y-0.5">
                    <p>
                      {result.cohort.lead.full_name ?? "—"}
                      {result.cohort.lead.converted && (
                        <span className="ml-1 text-emerald-600">
                          · converted
                        </span>
                      )}
                    </p>
                    <p className="text-gray-400">
                      {result.cohort.lead.phone_number ?? "—"}
                      {result.cohort.lead.email
                        ? ` · ${result.cohort.lead.email}`
                        : ""}
                    </p>
                    <p className="text-gray-400">
                      created{" "}
                      {new Date(
                        result.cohort.lead.created_at,
                      ).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">
                    No lead record found for this phone.
                  </p>
                )}
              </div>

              {/* Onboarding bundle price (cohort-resolved) for this product+region. */}
              <div className="border-t border-gray-100 pt-2">
                <p className="text-[11px] font-medium text-gray-500 mb-1">
                  Onboarding bundle
                </p>
                {result.onboarding ? (
                  <p className="text-sm text-gray-800">
                    {result.onboarding.name}{" "}
                    <span className="font-semibold">
                      {result.onboarding.currency}{" "}
                      {result.onboarding.amount.toLocaleString()}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {" "}
                      · this customer&apos;s price
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    No onboarding bundle configured for this product in this
                    region.
                  </p>
                )}
              </div>
            </div>

            {/* The link. */}
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-600">
                  Shareable link
                </p>
                <Button size="sm" variant="outline" onClick={copy}>
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs font-mono break-all text-gray-700">
                {result.link}
              </p>
            </div>
          </div>
        )}
      </div>
    </HasAccess>
  );
}
